import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Badge } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  AuditOutlined,
  TeamOutlined,
  MonitorOutlined,
  BookOutlined,
  DollarOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  UserOutlined,
  SolutionOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { toggleCollapsed, selectCollapsed } from '../../store/slices/menuSlice';
import { clearUser, selectUserInfo } from '../../store/slices/userSlice';
import { MENU_ITEMS, ROUTES, getMenuItemsByRole } from '../../router/routes';
import { getSupplierCertPendingCount, getUserCertPendingCount } from '../../api/life';
import { getUserInfo, getUserRole, clearAuth } from '../../utils/auth';
import './index.css';

const { Header, Sider, Content } = Layout;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectCollapsed);
  const userInfo = useSelector(selectUserInfo);

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  // 供应商认证待审核数（社区运营菜单角标）
  const [certPendingCount, setCertPendingCount] = useState(0);
  // 实名认证待复审数（社区运营菜单角标）
  const [userCertPendingCount, setUserCertPendingCount] = useState(0);

  const currentRole = getUserRole();
  const filteredMenuItems = currentRole ? getMenuItemsByRole(currentRole) : MENU_ITEMS;

  useEffect(() => {
    const info = getUserInfo();
    if (info) {
      dispatch(clearUser());
      dispatch({ type: 'user/setUserInfo', payload: info });
    }
  }, [dispatch]);

  // 拉取待审核认证数，作为侧边菜单徽标（失败静默，不影响布局）
  useEffect(() => {
    let mounted = true;
    getSupplierCertPendingCount()
      .then((res) => {
        if (!mounted) return;
        const value = typeof res.data === 'object' ? (res.data.count ?? res.data.pendingCount) : res.data;
        setCertPendingCount(Number(value) || 0);
      })
      .catch(() => {});
    // 实名认证待复审数（data 为 Long 直返）
    getUserCertPendingCount()
      .then((res) => {
        if (!mounted) return;
        setUserCertPendingCount(Number(res.data) || 0);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const matchedKey = getSelectedKeyFromPath(path);
    if (matchedKey) {
      setSelectedKeys([matchedKey]);
      const parentKey = getParentKeyFromPath(path);
      if (parentKey) {
        setOpenKeys([parentKey]);
      }
    }
  }, [location.pathname]);

  const getSelectedKeyFromPath = (path) => {
    for (const item of filteredMenuItems) {
      if (item.path === path) return item.key;
      if (item.children) {
        for (const child of item.children) {
          if (child.path === path) return child.key;
        }
      }
    }
    return null;
  };

  const getParentKeyFromPath = (path) => {
    for (const item of filteredMenuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (child.path === path) return item.key;
        }
      }
    }
    return null;
  };

  const handleMenuClick = ({ key }) => {
    const menuItem = findMenuItemByKey(key);
    if (menuItem?.path) {
      navigate(menuItem.path);
    }
  };

  const findMenuItemByKey = (key, items = filteredMenuItems) => {
    for (const item of items) {
      if (item.key === key) return item;
      if (item.children) {
        const found = findMenuItemByKey(key, item.children);
        if (found) return found;
      }
    }
    return null;
  };

  const handleLogout = () => {
    clearAuth();
    dispatch(clearUser());
    navigate(ROUTES.LOGIN);
  };

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: '个人中心' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  const getIcon = (iconName) => {
    const icons = {
      Dashboard: <DashboardOutlined />,
      Audit: <AuditOutlined />,
      Team: <TeamOutlined />,
      Monitor: <MonitorOutlined />,
      Book: <BookOutlined />,
      Finance: <DollarOutlined />,
      Setting: <SettingOutlined />,
      Join: <SolutionOutlined />,
      Life: <HomeOutlined />,
    };
    return icons[iconName] || <DashboardOutlined />;
  };

  // 各认证审核菜单的待办角标数（菜单 key → 计数）
  const badgeCountMap = {
    life_supplier_certs: certPendingCount,
    life_user_certs: userCertPendingCount,
  };

  const menuItems = filteredMenuItems.map((item) => ({
    key: item.key,
    icon: getIcon(item.icon),
    label: item.label,
    children: item.children?.map((child) => ({
      key: child.key,
      // 认证审核类菜单展示待审核角标
      label:
        badgeCountMap[child.key] > 0 ? (
          <Badge count={badgeCountMap[child.key]} size="small" offset={[12, 0]}>
            {child.label}
          </Badge>
        ) : (
          child.label
        ),
    })),
  }));

  return (
    <Layout className="admin-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={220}
        className="admin-sider"
        theme="light"
      >
        <div className="logo-container">
          <div className="logo">
            {collapsed ? 'KCT' : '寻医问病平台'}
          </div>
        </div>
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          /* 受控展开：路由变化时自动展开当前组；点击时由 onOpenChange 同步手动展开/收起 */
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
          className="admin-menu"
        />
      </Sider>

      <Layout>
        <Header className="admin-header">
          <div className="header-left">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => dispatch(toggleCollapsed())}
              className="collapse-btn"
            />
          </div>
          <div className="header-right">
            <BellOutlined className="header-icon" />
            <Dropdown
              menu={{ items: userMenuItems, onClick: ({ key }) => key === 'logout' && handleLogout() }}
              placement="bottomRight"
            >
              <Avatar
                style={{ backgroundColor: '#4A90E2', cursor: 'pointer' }}
                icon={<UserOutlined />}
              />
            </Dropdown>
            <span className="user-name">{userInfo?.name || '管理员'}</span>
          </div>
        </Header>

        <Content className="admin-content">
          <div className="content-wrapper">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
